import type { Dispatch, ReactChild, ReactFragment, ReactPortal, SetStateAction } from 'react';
import { FormSection, Text } from '@ksyos/design-system';
import React, { Children, useEffect, useState } from 'react';
import type {
    IEmbloomQuestionnaire,
    IGroupSetting,
    IServiceConsumer,
    IServiceGroupSetting,
    IServiceOrganizationSettings,
    IServiceProvider,
} from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils/validation';
import { never } from '@common/utils/never';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { useServiceOrganizationSettingContext } from '@frontend/utils/hooks/useServiceOrganizationsSettingsContext';
import type {
    IServiceGraderPoolOption,
    IServiceOrganizationSettingFormSectionProps,
} from './ServiceOrganizationSetting';
import type { SettingsKey } from './serviceOrganizationSettingsComponentsList';
import { ServiceOrganizationSettingFormMode } from './ServiceOrganizationSetting';
import { AnalystOrganizationFormSection } from './ServiceOrganizationSettingFormSections/AnalystOrganizationFromSection';
import { DiagnosticEquipmentFormSection } from './ServiceOrganizationSettingFormSections/DiagnosticEquipmentFormSection';
import { EmbloomQuestionnairesFormSection } from './ServiceOrganizationSettingFormSections/EmbloomQuestionnairesFormSection';
import { GraderPoolFormSection } from './ServiceOrganizationSettingFormSections/GraderPoolFormSection';
import { GroupFormSection } from './ServiceOrganizationSettingFormSections/GroupFormSection';
import { HideRequestedExaminationTaskFeatureFormSection } from './ServiceOrganizationSettingFormSections/HideRequestedExaminationTaskFeatureFormSection';
import { RecipientIsOwnOrganizationFormSection } from './ServiceOrganizationSettingFormSections/RecipientIsOwnOrganizationSection';
import { RequestRecipientFormSection } from './ServiceOrganizationSettingFormSections/RequestRecipientFormSection';
import { SendingHolter } from './ServiceOrganizationSettingFormSections/SendingHolter';
import { SoftwareFormSection } from './ServiceOrganizationSettingFormSections/SoftwareFormSection';
import { SomnologyCanReferPatientWithoutSmartphoneFormSection } from './ServiceOrganizationSettingFormSections/SomnologyCanReferPatientWithoutSmartphoneFormSection';
import { SomnologyOrganizationTypeFormSection } from './ServiceOrganizationSettingFormSections/SomnologyOrganizationTypeFormSection';
import { ZorgmailFormSection } from './ServiceOrganizationSettingFormSections/ZorgmailFormSection';
import { serviceOrganizationSettingsComponentsList } from './serviceOrganizationSettingsComponentsList';

interface IServiceOrganizationSettingSettingsListProps {
    currentServiceGroup: IServiceGroupSetting | null;
    embloomQuestionnaires: { embloomQuestionnaire: IEmbloomQuestionnaire }[];
    errors: ValidationErrors;
    handleChange<K extends keyof IServiceOrganizationSettings>(name: K, value: IServiceOrganizationSettings[K]): void;
    hasServiceProviders: boolean;
    mode: ServiceOrganizationSettingFormMode;
    serviceOrganizationSettingEditVersion: IServiceOrganizationSettings | null;
    setContainsServiceOrganizationSettings: Dispatch<SetStateAction<boolean>>;
    setCurrentServiceGroup: Dispatch<SetStateAction<IServiceGroupSetting | null>>;
    setEmbloomQuestionnaires: Dispatch<SetStateAction<{ embloomQuestionnaire: IEmbloomQuestionnaire }[]>>;
    setHasServiceProviders: Dispatch<SetStateAction<boolean>>;
    setMode: Dispatch<SetStateAction<ServiceOrganizationSettingFormMode>>;
    validateOne(name: keyof IServiceOrganizationSettings): void;
}

export function ServiceOrganizationSettingSettingsList(props: IServiceOrganizationSettingSettingsListProps) {
    const { serviceOrganizationSetting } = useServiceOrganizationSettingContext();
    const {
        currentServiceGroup,
        embloomQuestionnaires,
        errors,
        handleChange,
        hasServiceProviders,
        mode,
        serviceOrganizationSettingEditVersion,
        setContainsServiceOrganizationSettings,
        setCurrentServiceGroup,
        setEmbloomQuestionnaires,
        setHasServiceProviders,
        setMode,
        validateOne,
    } = props;

    const [showAddEmbloomQuestionnaireModal, setShowAddEmbloomQuestionnaireModal] = useState(false);
    const [hasServiceConsumers, setHasServiceConsumers] = useState(false);
    const [hasBiometricians, setHasBiometricians] = useState(false);
    const [hasRequestRecipientProviders, setHasRequestRecipientProviders] = useState(false);
    const [serviceGroupSettings, setServiceGroupSettings] = useState<IGroupSetting[]>([]);
    const [serviceGraderPools, setServiceGraderPools] = useState<IServiceGraderPoolOption[]>([]);

    const componentsList = createComponentList(
        serviceOrganizationSettingsComponentsList(serviceOrganizationSetting.service.serviceType)
    );

    useEffect(() => {
        loadServiceConsumers(serviceOrganizationSetting.service.id, serviceOrganizationSetting.organization.id);
        loadServiceProviders(serviceOrganizationSetting.service.id, serviceOrganizationSetting.organization.id);
    }, [serviceOrganizationSetting.service.id, serviceOrganizationSetting.organization.id]);

    useEffect(() => {
        setMode(ServiceOrganizationSettingFormMode.read);
        loadServiceGroupSettings(serviceOrganizationSetting.service.id);
        loadServiceGraderPools(serviceOrganizationSetting.service.id);
    }, [serviceOrganizationSetting.service.id]);

    useEffect(() => {
        setContainsServiceOrganizationSettings(componentsList.length > 0);
    }, [componentsList.length]);

    return <>{renderComponentList(componentsList)}</>;

    function renderComponentList(elements: (ReactChild | ReactFragment | ReactPortal)[]) {
        if (elements.length === 0) {
            return (
                <FormSection isBorderless={true}>
                    <Text key={`${serviceOrganizationSetting.serviceId}-emptyList`}>
                        {translateKey('carePath.noServiceOrganizationSettings')}
                    </Text>
                </FormSection>
            );
        }

        return elements;
    }

    function createComponentList(settingsKeys: SettingsKey[] | undefined) {
        if (!settingsKeys) {
            return Children.toArray([]);
        }

        const settingsToRender = Children.toArray(
            settingsKeys
                .map((settingsKey) => renderComponent(settingsKey, { ...getPropsForFormSection() }))
                .map((component) => {
                    if (component && component.component) {
                        return <React.Fragment key={component.key}>{component.component}</React.Fragment>;
                    }
                })
        ).filter(Boolean);

        return settingsToRender;
    }

    function renderComponent(
        settingsKey: SettingsKey,
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps
    ) {
        switch (settingsKey) {
            case 'hideRequestedExaminationTaskFeature':
                return renderHideRequestedExaminationTaskFeature(serviceOrganizationSettings, settingsKey);
            case 'recipientIsOwnOrganization':
                return renderRecipientIsOwnOrganization(serviceOrganizationSettings, settingsKey);
            case 'requestRecipient':
                return renderRequestRecipient(serviceOrganizationSettings, settingsKey);
            case 'sendingHolter':
                return renderSendingHolter(serviceOrganizationSettings, settingsKey);
            case 'serviceGraderPool':
                return renderServiceGraderPool(serviceOrganizationSettings, settingsKey);
            case 'ksyosHolterAnalystOrganization':
                return renderKsyosHolterAnalystOrganization(serviceOrganizationSettings, settingsKey);
            case 'somnologyOrganization':
                return renderSomnologyOrganization(serviceOrganizationSettings, settingsKey);
            case 'somnologyCanReferPatientWithoutSmartphone':
                return renderSomnologyCanReferPatientWithoutSmartphone(serviceOrganizationSettings, settingsKey);
            case 'zorgMail':
                return renderZorgmail(serviceOrganizationSettings, settingsKey);
            case 'software':
                return renderSoftware(serviceOrganizationSettings, settingsKey);
            case 'embloomQuestionnaires':
                return renderEmbloomQuestionnaires(serviceOrganizationSettings, settingsKey);
            case 'serviceGroupSetting':
                return renderServiceGroupSetting(serviceOrganizationSettings, settingsKey);
            case 'diagnosticEquipment':
                return renderDiagnosticEquipment(serviceOrganizationSettings, settingsKey);
            default:
                return never(settingsKey);
        }
    }

    function renderHideRequestedExaminationTaskFeature(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const hasActiveBiometrician = hasServiceProviders && hasBiometricians;

        const hideRequestedExaminationTaskFeatureFormSection = HideRequestedExaminationTaskFeatureFormSection({
            ...serviceOrganizationSettings,
            hasActiveBiometrician,
        });

        return {
            component: hideRequestedExaminationTaskFeatureFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderRecipientIsOwnOrganization(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const recipientIsOwnOrganizationFormSection = RecipientIsOwnOrganizationFormSection({
            ...serviceOrganizationSettings,
            hasServiceConsumers,
            hasServiceProviders,
        });

        return {
            component: recipientIsOwnOrganizationFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderRequestRecipient(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const isRecipientIsOwnOrganization =
            hasServiceConsumers && hasServiceProviders && serviceOrganizationSetting.recipientIsOwnOrganization;

        const requestRecipientFormSection = RequestRecipientFormSection({
            ...serviceOrganizationSettings,
            hasRequestRecipientProviders,
            isRecipientIsOwnOrganization,
        });

        return {
            component: requestRecipientFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderSendingHolter(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const requestRecipientFormSection = SendingHolter({
            ...serviceOrganizationSettings,
        });

        return {
            component: requestRecipientFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderServiceGraderPool(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const graderPoolFormSection = GraderPoolFormSection({
            ...serviceOrganizationSettings,
            serviceGraderPools,
        });

        return {
            component: graderPoolFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderKsyosHolterAnalystOrganization(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const ksyosHolterAnalystOrganizationFormSection = AnalystOrganizationFormSection({
            ...serviceOrganizationSettings,
        });

        return {
            component: ksyosHolterAnalystOrganizationFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderSomnologyOrganization(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const somnologyOrganizationTypeFormSection = SomnologyOrganizationTypeFormSection({
            ...serviceOrganizationSettings,
        });

        return {
            component: somnologyOrganizationTypeFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderSomnologyCanReferPatientWithoutSmartphone(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const somnologyCanReferPatientWithoutSmartphoneFormSection =
            SomnologyCanReferPatientWithoutSmartphoneFormSection({
                ...serviceOrganizationSettings,
            });

        return {
            component: somnologyCanReferPatientWithoutSmartphoneFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderZorgmail(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const zorgmailFormSection = ZorgmailFormSection({
            ...serviceOrganizationSettings,
        });

        return {
            component: zorgmailFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderSoftware(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const softwareFormSection = SoftwareFormSection({
            ...serviceOrganizationSettings,
        });

        return {
            component: softwareFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderEmbloomQuestionnaires(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const embloomQuestionnairesFormSection = EmbloomQuestionnairesFormSection({
            ...serviceOrganizationSettings,
            embloomQuestionnaires,
            loadStandardEmbloomQuestionnaires,
            setEmbloomQuestionnaires,
            setShowAddEmbloomQuestionnaireModal,
            showAddEmbloomQuestionnaireModal,
        });

        return {
            component: embloomQuestionnairesFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderServiceGroupSetting(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const serviceGroupFormSection = GroupFormSection({
            ...serviceOrganizationSettings,
            currentServiceGroup,
            serviceGroupSettingOptions: serviceGroupSettings as unknown as IServiceGroupSetting[],
            setCurrentServiceGroup,
        });

        return {
            component: serviceGroupFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function renderDiagnosticEquipment(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        settingsKey: SettingsKey
    ) {
        const diagnosticEquipmentFormSection = DiagnosticEquipmentFormSection({
            canPerformGlaucoomExamination: serviceOrganizationSettings.canPerformGlaucoomExamination,
            handleChange: (value) => handleChange('canPerformGlaucoomExamination', value),
            mode: serviceOrganizationSettings.mode,
            serviceCode: serviceOrganizationSettings.service.code,
        });

        return {
            component: diagnosticEquipmentFormSection,
            key: generateComponentKey(serviceOrganizationSettings, settingsKey),
        };
    }

    function generateComponentKey(
        serviceOrganizationSettings: IServiceOrganizationSettingFormSectionProps,
        key: SettingsKey
    ) {
        return `${key}-${serviceOrganizationSettings.service.code}`;
    }

    function getPropsForFormSection(): IServiceOrganizationSettingFormSectionProps {
        const propsForFormSection = {
            errors,
            handleChange,
            hasBiometricians,
            hasServiceProviders,
            mode,
            validateOne,
        };
        if (mode === ServiceOrganizationSettingFormMode.edit && serviceOrganizationSettingEditVersion) {
            return { ...propsForFormSection, ...serviceOrganizationSettingEditVersion };
        } else {
            return { ...propsForFormSection, ...serviceOrganizationSetting };
        }
    }

    async function loadServiceProviders(serviceId: string, organizationId: string) {
        const result = await fetchJson(
            `/api/serviceProviders?serviceId=${serviceId}&isOrWillBecomeActive=${true}&organizationId=${organizationId}&includeServiceGroups=true`,
            'GET'
        );
        const serviceProviders = result.json.items as IServiceProvider[];
        const hasProviders = serviceProviders.length > 0;

        setHasBiometricians(
            hasProviders &&
                serviceProviders.some((serviceProvider: IServiceProvider) => serviceProvider.isBiometrician === true)
        );
        setHasRequestRecipientProviders(
            hasProviders &&
                serviceProviders.some(
                    (serviceProvider: IServiceProvider) => serviceProvider.isRequestRecipient === true
                )
        );
        setHasServiceProviders(hasProviders);
    }

    async function loadServiceConsumers(serviceId: string, organizationId: string) {
        const result = await fetchJson(
            `/api/serviceConsumers?serviceId=${serviceId}&isActive=${true}&organizationId=${organizationId}`,
            'GET'
        );
        const serviceConsumers = result.json.items as IServiceConsumer[];
        setHasServiceConsumers(serviceConsumers.length > 0);
    }

    async function loadServiceGroupSettings(serviceId: string) {
        const result = await fetchJson(`/api/serviceGroupSettings?serviceId=${serviceId}&isActive=true`, 'GET');
        setServiceGroupSettings(result.json.items);
    }

    async function loadStandardEmbloomQuestionnaires() {
        const result = await fetchJson('/api/embloomQuestionnaires', 'GET');
        const standardEmbloomQuestionnaires = result.json.items
            .filter((embloomQuestionnaire: IEmbloomQuestionnaire) => embloomQuestionnaire.isStandard === true)
            .map((questionnaire: IEmbloomQuestionnaire) => ({ embloomQuestionnaire: questionnaire }));
        setEmbloomQuestionnaires(standardEmbloomQuestionnaires);
    }

    async function loadServiceGraderPools(serviceId: string) {
        const result = (await fetchJson(`/api/serviceGraderPools?serviceId=${serviceId}`, 'GET')).json.items;
        setServiceGraderPools(result);
    }
}
